import React, { useState, useEffect, useRef } from 'react';
import { Send, AttachFile, BarChart, DeleteOutline, HistoryOutlined, Lightbulb, Refresh } from '@mui/icons-material';
import MarkdownRenderer from './MarkdownRenderer';
import './markdown-styles.css';

const RM360ChatbotView = () => {
  const [messages, setMessages] = useState([
    { id: 1, text: "Welcome to RM360 Assistant! I can help you analyze customer data and provide insights. Please select an RM to begin.", sender: 'bot', timestamp: new Date() }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedRM, setSelectedRM] = useState('');
  const [rmOptions, setRmOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Sample query suggestions
  const querySuggestions = [
    "What are the top 5 customers by revenue?",
    "Show me the performance trend for this quarter",
    "Which products should I recommend to my customers?",
    "Analyze customer purchase patterns",
    "Compare this month's sales with last month"
  ];

  // Fetch RM options on component mount
  useEffect(() => {
    fetchRMOptions();
  }, []);

  // Auto scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchRMOptions = async () => {
    try {
      // Fetch RM options directly from NBP-1 backend API
      const response = await fetch('https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net/api/rm-list');
      if (!response.ok) {
        throw new Error('Could not fetch RM options');
      }
      const data = await response.json();
      // NBP-1 returns {status: 'success', rm_list: [...]}
      setRmOptions(data.rm_list || []);
    } catch (error) {
      console.error('Error fetching RM options:', error);
      // Fallback to sample data
      setRmOptions(['RM001', 'RM002', 'RM003']);
    }
  };

  const fetchRMOptionsFromBackend = async () => {
    try {
      // Fetch RM options from NBP-1 backend API
      const response = await fetch('https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net/api/rm-list');
      if (!response.ok) {
        throw new Error('Could not fetch RM options');
      }
      const data = await response.json();
      // NBP-1 returns {status: 'success', rm_list: [...]}
      setRmOptions(data.rm_list || []);
    } catch (error) {
      console.error('Error fetching RM options from API:', error);
      // Fallback to sample data
      setRmOptions(['RM001', 'RM002', 'RM003']);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !selectedRM) return;
    
    // Add user message to chat
    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      // Send message to NBP-1 backend
      const response = await fetch('https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          rm_name: selectedRM
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Handle NBP-1 response format: {response, has_chart, chart_url?, type}
      if (data.type === 'visualization' && data.has_chart && data.chart_url) {
        // NBP-1 returns relative path, construct full URL
        const visualizationUrl = `https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net${data.chart_url}`;
        addBotMessage("Here's a visualization based on your query:", { visualizationUrl });
      } else if (data.response) {
        // Text response from NBP-1
        addBotMessage(data.response);
      } else {
        // Fallback
        addBotMessage("I processed your request.");
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = formatErrorMessage(error);
      addBotMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryAnalysis = async () => {
    if (!inputMessage.trim() || !selectedRM) return;
    
    // Add user message to chat
    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      // Send query to NBP-1 backend for analysis
      const response = await fetch('https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          rm_name: selectedRM
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Handle NBP-1 response format
      if (data.type === 'visualization' && data.has_chart && data.chart_url) {
        const visualizationUrl = `https://nbps-ezephtebhhasead8.centralindia-01.azurewebsites.net${data.chart_url}`;
        addBotMessage('Here\'s a visualization based on your analysis:', { visualizationUrl });
      } else if (data.response) {
        addBotMessage(data.response);
      } else {
        addBotMessage('Analysis complete.');
      }
    } catch (error) {
      console.error('Error during analysis:', error);
      const errorMessage = formatErrorMessage(error);
      addBotMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatErrorMessage = (error) => {
    try {
      const errorMessage = error.message || error.toString();
      
      // Check for timeout errors
      if (errorMessage.includes('timeout') || errorMessage.includes('TIMEOUT')) {
        return "Request timed out, try again";
      }
      
      // Check for network errors
      if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
        return "Network error, please check your connection and try again";
      }
      
      // Check for server errors (500, 400, etc.)
      if (errorMessage.includes('Server responded with')) {
        return "Please try it again, there was error processing the request";
      }
      
      // Default error message
      return "Please try it again, there was error processing the request";
      
    } catch (parseError) {
      return "Please try it again, there was error processing the request";
    }
  };

  const addBotMessage = (text, additionalData = {}) => {
    const botMessage = {
      id: messages.length + 1,
      text: text,
      sender: 'bot',
      timestamp: new Date(),
      ...additionalData
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearChat = () => {
    setMessages([
      { id: 1, text: "Welcome to RM360 Assistant! I can help you analyze customer data and provide insights. Please select an RM to begin.", sender: 'bot', timestamp: new Date() }
    ]);
    setShowSuggestions(true);
  };
  
  const handleQuerySuggestion = (suggestion) => {
    setInputMessage(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div className="h-full flex flex-col p-6 bg-[#0A0A0A]">
      {/* Header Controls */}
      <div className="flex justify-between items-center mb-6 p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
        <div className="flex items-center space-x-4">
          <button
            onClick={clearChat}
            className="p-3 rounded-xl hover:bg-[#2A2A2A] focus:outline-none flex items-center text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200"
            title="Clear chat history"
          >
            <DeleteOutline className="mr-2 text-[#B4E50D]" />
            <span className="text-sm font-medium">Clear</span>
          </button>
          <div className="flex items-center">
            <label htmlFor="rm-select" className="mr-3 font-medium text-[#FFFFFF]">Select RM:</label>
            <select
              id="rm-select"
              value={selectedRM}
              onChange={(e) => setSelectedRM(e.target.value)}
              className="border border-[#3A3A3A] rounded-xl p-3 bg-[#2A2A2A] text-[#FFFFFF] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200 min-w-[200px]"
            >
              <option value="">-- Select an RM --</option>
              <option value="Saif Tawfiq">Saif Tawfiq</option>
              <option value="Ahmad Al-Tamimi">Ahmad Al-Tamimi</option>
              <option value="Nader Darwish">Nader Darwish</option>
              <option value="Layla Shuman">Layla Shuman</option>
              <option value="Zaid Al-Masri">Zaid Al-Masri</option>
              <option value="Saja Al-Dabbas">Saja Al-Dabbas</option>
              <option value="Mona Ibrahim">Mona Ibrahim</option>
              <option value="Leen Abu Zaid">Leen Abu Zaid</option>
              <option value="Fadi Al-Omari">Fadi Al-Omari</option>
              <option value="Iyad Al-Khatib">Iyad Al-Khatib</option>
              <option value="Heba Saba">Heba Saba</option>
              <option value="Tarek Al-Farhan">Tarek Al-Farhan</option>
              <option value="Rami Al-Haj">Rami Al-Haj</option>
              <option value="Nour Badran">Nour Badran</option>
              <option value="Firas Najjar">Firas Najjar</option>
              <option value="Omar Haddad">Omar Haddad</option>
              <option value="Dana Hamdan">Dana Hamdan</option>
              <option value="Sami Al-Rawi">Sami Al-Rawi</option>
              <option value="Hala Qassem">Hala Qassem</option>
              <option value="Khaled Jaber">Khaled Jaber</option>
              <option value="Reem Taha">Reem Taha</option>
              <option value="Yasmin Qadri">Yasmin Qadri</option>
              <option value="Basel Al-Amin">Basel Al-Amin</option>
              <option value="Rana Khalil">Rana Khalil</option>
              <option value="Salma Barakat">Salma Barakat</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Main Chat Container */}
      <div className="flex-1 bg-[#1A1A1A] rounded-xl shadow-2xl overflow-hidden flex flex-col border border-[#2A2A2A]">
        {/* Messages container */}
        <div className="flex-1 overflow-y-auto p-6 bg-[#1A1A1A]">
          {messages.map((message) => (
            <div key={message.id}>
              <div
                className={`mb-6 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-xl p-4 ${
                    message.sender === 'user'
                      ? 'bg-[#B4E50D] text-[#0A0A0A]'
                      : 'bg-[#2A2A2A] border border-[#3A3A3A] text-[#FFFFFF]'
                  }`}
                >
                  {message.sender === 'user' ? (
                    <div className="text-sm font-medium">{message.text}</div>
                  ) : (
                    <div className={`text-sm ${message.sender === 'user' ? '' : 'text-[#FFFFFF]'}`}>
                      <MarkdownRenderer content={message.text} />
                    </div>
                  )}
                  <div
                    className={`text-xs mt-3 flex items-center justify-between ${
                      message.sender === 'user' ? 'text-[#0A0A0A] opacity-80' : 'text-[#B0B0B0]'
                    }`}
                  >
                    <span>{formatTimestamp(message.timestamp)}</span>
                    {message.sender === 'bot' && (
                      <span className="flex items-center text-[#B4E50D]">
                        <Refresh fontSize="small" className="mr-1" />
                        RM360 
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Visualization display for this message */}
              {message.visualizationUrl && (
                <div className="mb-6 flex justify-start">
                  <div className="max-w-full w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl p-4">
                    <iframe
                      src={message.visualizationUrl}
                      title="Data Visualization"
                      className="w-full h-96 border-none rounded-lg"
                    />
                  </div>
                </div>
              )}

              {/* Table display for this message */}
              {message.tableData && message.tableData.data && (
                <div className="mb-6 flex justify-start">
                  <div className="max-w-full w-full bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl p-4 overflow-x-auto">
                    <div className="text-sm font-medium mb-4 text-[#B4E50D]">{message.tableData.description || 'Query Results'}</div>
                    <table className="min-w-full divide-y divide-[#3A3A3A]">
                      <thead className="bg-[#1A1A1A]">
                        <tr>
                          {message.tableData.columns.map((column, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-[#B4E50D] uppercase tracking-wider"
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-[#2A2A2A] divide-y divide-[#3A3A3A]">
                        {message.tableData.data.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-[#3A3A3A] transition-colors">
                            {message.tableData.columns.map((column, colIndex) => (
                              <td
                                key={`${rowIndex}-${colIndex}`}
                                className="px-6 py-4 whitespace-nowrap text-sm text-[#FFFFFF]"
                              >
                                {row[column]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}


          {isLoading && (
            <div className="flex justify-center items-center my-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B4E50D]"></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input area */}
        <div className="border-t border-[#2A2A2A] p-6 bg-[#1A1A1A]">
          <div className="flex items-center">
            <button
              className="p-3 rounded-xl hover:bg-[#2A2A2A] focus:outline-none text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200"
              title="Attach file"
            >
              <AttachFile />
            </button>
            <div className="flex-1 mx-3">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={selectedRM ? "Type your message or ask for data analysis..." : "Please select an RM first"}
                disabled={!selectedRM}
                className="w-full border border-[#3A3A3A] rounded-xl p-3 bg-[#2A2A2A] text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 resize-none transition-all duration-200"
                rows="2"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleQueryAnalysis}
                disabled={!inputMessage.trim() || !selectedRM || isLoading}
                className="p-3 rounded-xl hover:bg-[#2A2A2A] focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Run data analysis"
              >
                <BarChart className={`${!inputMessage.trim() || !selectedRM || isLoading ? 'text-[#808080]' : 'text-[#B4E50D]'}`} />
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || !selectedRM || isLoading}
                className="p-3 rounded-xl bg-[#B4E50D] hover:shadow-lg focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                title="Send message"
              >
                <Send className="text-[#0A0A0A]" />
              </button>
            </div>
          </div>
          
          {!selectedRM && (
            <div className="mt-3 text-center text-sm text-[#B0B0B0]">
              Please select an RM from the dropdown to start chatting
            </div>
          )}
          
          {selectedRM && showSuggestions && messages.length < 3 && (
            <div className="mt-6">
              <div className="flex items-center text-sm text-[#B0B0B0] mb-3">
                <Lightbulb fontSize="small" className="mr-2 text-[#B4E50D]" />
                <span>Try asking:</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {querySuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuerySuggestion(suggestion)}
                    className="text-left text-sm p-3 border border-[#3A3A3A] rounded-xl hover:bg-[#2A2A2A] hover:border-[#B4E50D] transition-all duration-200 text-[#FFFFFF] hover:text-[#B4E50D]"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RM360ChatbotView;
